import axios from 'axios';
import { getAuthToken } from './TokenService'; 

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

export const FileUploadAPI = {
  async uploadFile(file, options = {}, signal = null, retries = 4) {
    const token = await getAuthToken();
    try {
      const bodyFormData = new FormData();
      bodyFormData.append('files', file);
      bodyFormData.append('purpose', options.purpose || 'assistants');
      bodyFormData.append('path', options.path || '/');
      bodyFormData.append('extract_images', options.extract_images || 'false');
      
      const response = await axios.post('https://chat.int.bayer.com/api/v2/files', bodyFormData, {
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        signal: signal 
      });
      
      return response.data;
    } catch (error) {
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        throw error;
      }
      if (retries > 0) {
        await delay(5000); 
        return this.uploadFile(file, options, signal, retries - 1);
      }
      throw error;
    }
  }, 

  async checkFileExists(fileId, signal = null) {
    try {
      return await this.checkFileSSE(fileId, signal, 2);
    } catch (error) {
      if (error.name === 'AbortError' || signal?.aborted) {
        throw error;
      }
      return await this.checkFileV2(fileId, signal); 
    }
  },

  async checkFileSSE(fileId, signal = null, retries = 2) {
    try {
      const token = await getAuthToken();
      const response = await fetch(`https://chat.int.bayer.com/api/v3/files/${fileId}/status`, {
        headers: {
          'accept': 'text/event-stream',
          'Authorization': `Bearer ${token}`
        },
        signal: signal
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        if (signal?.aborted) {
          reader.cancel();
          throw new Error('AbortError');
        }

        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = JSON.parse(line.slice(6));
            if (data.status === 'finished') {
              reader.cancel();
              return 'finished';
            }
            if (data.status === 'failed' || data.status === 'error') {
              reader.cancel();
              throw new Error('File processing failed');
            }
          }
        }
      }
    } catch (error) {
      if (error.name === 'AbortError' || error.message === 'AbortError' || signal?.aborted) {
        throw error;
      }
      if (retries > 0) {
        await delay(5000);
        return this.checkFileSSE(fileId, signal, retries - 1);
      }
      throw error;
    }
  },

  async checkFileV2(fileId, signal = null) {
    while (true) {
      if (signal?.aborted) {
        throw new Error('AbortError');
      }

      try {
        const token = await getAuthToken();
        const response = await axios.get(`https://chat.int.bayer.com/api/v2/files/${fileId}`, {
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          signal: signal
        });

        const data = response.data;
        if (data.status === 'finished') {
          return 'finished';
        }
        if (data.status === 'failed' || data.status === 'error') {
          throw new Error("Uploading the file has failed, try again later.");
        }
      } catch (error) {
        if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED' || signal?.aborted) {
          throw error;
        }
      }

      await delay(10000);
    }
  }
};